import React, { useEffect, useMemo, useState } from 'react';
import { FaBolt, FaCheck, FaTimes } from 'react-icons/fa';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import useDailyProgress from '../../hooks/useDailyProgress';
import { formatDayStreakLabel } from '../../utils/dayStreak';
import { ReactComponent as EditPencilIcon } from '../../Icons/edit.svg';
import './HomeProgressFooter.css';
import './DailyGoalHeader.css';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const GOAL_PRESETS = [
  { value: 3, label: 'Casual' },
  { value: 5, label: 'Consistent' },
  { value: 10, label: 'Focused' },
  { value: 15, label: 'Elite' },
  { value: 20, label: 'Beast' },
];

function getCurrentWeekDays(today = new Date()) {
  const dayOfWeek = today.getDay();
  const monday = new Date(today);
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  monday.setDate(today.getDate() + diff);

  return WEEKDAY_LABELS.map((label, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return {
      label,
      dateKey: d.toDateString(),
      isToday: d.toDateString() === today.toDateString(),
    };
  });
}

function formatProjectionNumber(n) {
  return n.toLocaleString('en-US');
}

function getGoalProjectionTooltip(snippetsPerDay) {
  const week = snippetsPerDay * 7;
  const month = snippetsPerDay * 30;
  const year = snippetsPerDay * 365;
  return `${snippetsPerDay} snippets/day\n≈${formatProjectionNumber(week)} per week\n≈${formatProjectionNumber(month)} per month\n≈${formatProjectionNumber(year)} per year`;
}

function getStreakDateKeys(dailyProgress, displayStreak, today = new Date()) {
  if (displayStreak <= 0) {
    return new Set();
  }

  const map = dailyProgress && typeof dailyProgress === 'object' ? dailyProgress : {};
  const studiedToday = (map[today.toDateString()] || 0) > 0;
  const startDate = new Date(today);

  if (!studiedToday) {
    startDate.setDate(startDate.getDate() - 1);
  }

  const keys = new Set();
  const cursor = new Date(startDate);

  for (let i = 0; i < displayStreak; i++) {
    const key = cursor.toDateString();
    if ((map[key] || 0) > 0) {
      keys.add(key);
      cursor.setDate(cursor.getDate() - 1);
    } else {
      break;
    }
  }

  return keys;
}

function dailyGoalTrackFillGradient() {
  return { start: '#38bdf8', end: '#3b82f6' };
}

function HomeProgressWeekGrid({
  weekDays,
  dailyProgress,
  streakDateKeys,
  streakAtRisk,
  className = '',
}) {
  return (
    <div className={`header-goal-modal__week-tracker ${className}`.trim()}>
      <div className="header-goal-modal__week-grid">
        {weekDays.map((day) => {
          const dayStudied = (dailyProgress[day.dateKey] || 0) > 0;
          const inStreak = streakDateKeys.has(day.dateKey);
          return (
            <div
              key={day.dateKey}
              className={`header-goal-modal__week-day${day.isToday ? ' header-goal-modal__week-day--today' : ''}`}
            >
              <span
                className={`header-goal-modal__week-day-label${day.isToday ? ' header-goal-modal__week-day-label--today' : ''}`}
              >
                {day.label}
              </span>
              <span
                className={[
                  'header-goal-modal__week-node',
                  inStreak ? 'header-goal-modal__week-node--streak' : '',
                  dayStudied && !inStreak ? 'header-goal-modal__week-node--studied' : '',
                  day.isToday && !dayStudied && streakAtRisk
                    ? 'header-goal-modal__week-node--at-risk'
                    : '',
                  day.isToday ? 'header-goal-modal__week-node--today' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {inStreak && <FaCheck className="header-goal-modal__week-check" aria-hidden />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const HomeProgressFooter = ({ userId }) => {
  const {
    dailyProgress,
    dailyStudyGoal,
    completedToday,
    displayStreak,
    streakAtRisk,
    loading,
  } = useDailyProgress(userId, { enabled: Boolean(userId) });

  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState(5);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    setNewGoal(dailyStudyGoal);
  }, [dailyStudyGoal]);

  const updateDailyGoal = async () => {
    if (!userId) return;
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        dailyStudyGoal: newGoal,
      });
      setShowGoalModal(false);
    } catch (error) {
      console.error('Error updating daily goal:', error);
    }
  };

  const isComplete = completedToday >= dailyStudyGoal;
  const pct = Math.min((completedToday / dailyStudyGoal) * 100, 100);
  const streakHasValue = displayStreak > 0;
  const streakLabel = formatDayStreakLabel(displayStreak);
  const fillGradient = dailyGoalTrackFillGradient();
  const weekDays = useMemo(() => getCurrentWeekDays(), []);
  const streakDateKeys = useMemo(
    () => getStreakDateKeys(dailyProgress, displayStreak),
    [dailyProgress, displayStreak]
  );
  const remainingToday = Math.max(dailyStudyGoal - completedToday, 0);
  const customGoalTooltip = getGoalProjectionTooltip(newGoal);

  const getProgressPercentage = () => Math.min((completedToday / dailyStudyGoal) * 100, 100);

  const getProgressColor = () => {
    const percentage = getProgressPercentage();
    if (percentage >= 100) return '#10b981';
    if (percentage >= 75) return '#3b82f6';
    if (percentage >= 50) return '#f59e0b';
    return '#ef4444';
  };

  if (loading) {
    return (
      <div className="home-progress-footer">
        <div className="home-progress-footer-content">
          <div className="home-progress-loading-state">Loading progress...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={`home-progress-footer ${isCollapsed ? 'collapsed' : ''}`}
        onClick={() => {
          if (isCollapsed) {
            setShowGoalModal(true);
          } else {
            setIsCollapsed(true);
          }
        }}
        title={isCollapsed ? 'Set Daily Goal' : 'Collapse Progress'}
        style={{ cursor: 'pointer' }}
      >
        <div className="home-progress-footer-content">
          {isCollapsed && (
            <div className="home-progress-summary">
              <span className="summary-text">
                <FaBolt
                  className={`summary-text__bolt-icon${completedToday === 0 ? ' summary-text__bolt-icon--pulse' : ''}`}
                  aria-hidden
                />
                Daily Goal: {completedToday}/{dailyStudyGoal}
                <EditPencilIcon className="summary-text__edit-icon" aria-hidden />
              </span>
            </div>
          )}

          {!isCollapsed && (
            <div
              className="home-progress-section home-daily-goal-section"
              onClick={(e) => {
                e.stopPropagation();
                setShowGoalModal(true);
              }}
              style={{ cursor: 'pointer' }}
              title="Click to set daily goal"
            >
              <div className="home-progress-section-header">
                <span className="home-progress-section-icon">
                  <FaBolt className="home-progress-section-icon__bolt" aria-hidden />
                </span>
                <span className="home-progress-section-title">DAILY GOAL</span>
                <div className="home-daily-goal-circle">
                  <svg viewBox="0 0 36 36" className="home-daily-goal-ring">
                    <path
                      className="home-daily-goal-ring-bg"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className="home-daily-goal-ring-fill"
                      stroke={getProgressColor()}
                      strokeDasharray={`${getProgressPercentage()}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="home-daily-goal-text">
                    <span className="home-daily-goal-current">{completedToday}</span>
                    <span className="home-daily-goal-separator">/</span>
                    <span className="home-daily-goal-target">{dailyStudyGoal}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showGoalModal && (
        <div
          className="header-goal-modal-overlay"
          onClick={() => setShowGoalModal(false)}
          role="presentation"
        >
          <div
            className="header-goal-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="header-goal-modal-title"
            aria-modal="true"
          >
            <div className="header-goal-modal__header">
              <div className="header-goal-modal__header-text">
                <h3 id="header-goal-modal-title" className="header-goal-modal__title">
                  <FaBolt className="header-goal-modal__title-icon" aria-hidden />
                  Study Streak
                </h3>
                <p className="header-goal-modal__subtitle">
                  Build consistency. Your streak grows when you complete at least one snippet a day.
                </p>
              </div>
              <button
                type="button"
                className="header-goal-modal__close"
                onClick={() => setShowGoalModal(false)}
                aria-label="Close"
              >
                <FaTimes aria-hidden />
              </button>
            </div>

            <div className="header-goal-modal__body">
              <section className="header-goal-modal__today" aria-label="Today's goal">
                <h4 className="header-goal-modal__section-title">Today&apos;s Goal</h4>
                <div className="header-goal-modal__today-body">
                  <div
                    className="header-goal-modal__progress-track"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={dailyStudyGoal}
                    aria-valuenow={completedToday}
                    aria-label={`${completedToday} of ${dailyStudyGoal} snippets completed`}
                  >
                    <div
                      className={`header-goal-modal__progress-fill${isComplete ? ' header-goal-modal__progress-fill--complete' : ''}`}
                      style={{
                        width: `${pct}%`,
                        '--header-goal-modal-fill-start': fillGradient.start,
                        '--header-goal-modal-fill-end': fillGradient.end,
                      }}
                    />
                  </div>
                  <div className="header-goal-modal__today-stats">
                    <span className="header-goal-modal__today-completed">
                      {completedToday} / {dailyStudyGoal} snippets
                    </span>
                    {isComplete ? (
                      <span className="header-goal-modal__today-remaining header-goal-modal__today-remaining--done">
                        Complete
                      </span>
                    ) : (
                      <span className="header-goal-modal__today-remaining">
                        {remainingToday} remaining today
                      </span>
                    )}
                  </div>
                </div>
              </section>

              <hr className="header-goal-modal__divider" />

              <section className="header-goal-modal__streak-col" aria-label="Study streak">
                <div className="header-goal-modal__streak-header">
                  <h4 className="header-goal-modal__section-title">Study Streak</h4>
                  <p
                    className={`header-goal-modal__streak-value${streakHasValue ? ' header-goal-modal__streak-value--active' : ''}`}
                  >
                    {streakHasValue ? streakLabel : 'No streak'}
                  </p>
                </div>

                <HomeProgressWeekGrid
                  weekDays={weekDays}
                  dailyProgress={dailyProgress}
                  streakDateKeys={streakDateKeys}
                  streakAtRisk={streakAtRisk}
                />
              </section>

              <hr className="header-goal-modal__divider" />

              <section className="header-goal-modal__goal-col" aria-label="Daily goal settings">
                <h4 className="header-goal-modal__section-title">Daily Goal</h4>

                <div className="header-goal-modal__presets">
                  {GOAL_PRESETS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      className={`header-goal-modal__preset${newGoal === value ? ' header-goal-modal__preset--selected' : ''}`}
                      onClick={() => setNewGoal(value)}
                      aria-pressed={newGoal === value}
                      title={getGoalProjectionTooltip(value)}
                    >
                      <span className="header-goal-modal__preset-value">{value}</span>
                      <span className="header-goal-modal__preset-label">{label}</span>
                      <span className="header-goal-modal__preset-tooltip" role="tooltip">
                        {value} snippets/day
                        <br />
                        ≈{formatProjectionNumber(value * 7)} per week
                        <br />
                        ≈{formatProjectionNumber(value * 30)} per month
                        <br />
                        ≈{formatProjectionNumber(value * 365)} per year
                      </span>
                    </button>
                  ))}
                </div>

                <div className="header-goal-modal__custom">
                  <label htmlFor="home-progress-custom-goal" className="header-goal-modal__custom-label">
                    Custom Goal
                  </label>
                  <input
                    id="home-progress-custom-goal"
                    type="number"
                    min="1"
                    max="50"
                    value={newGoal}
                    onChange={(e) => setNewGoal(parseInt(e.target.value, 10) || 1)}
                    className="header-goal-modal__custom-input"
                    title={customGoalTooltip}
                  />
                </div>
              </section>
            </div>

            <div className="header-goal-modal-actions">
              <button
                type="button"
                className="header-goal-cancel-btn"
                onClick={() => setShowGoalModal(false)}
              >
                Cancel
              </button>
              <button type="button" className="header-goal-save-btn" onClick={updateDailyGoal}>
                Save Goal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HomeProgressFooter;
